import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CreditCard, Loader2, RefreshCw, DollarSign, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminBilling = () => {
  const { token } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchBillingData = async () => {
    setIsLoading(true);
    try {
      // Fetch both transactions and summary in parallel
      const [txnResponse, summaryResponse] = await Promise.all([
        axios.get(`${API}/admin/billing/transactions`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/billing/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setTransactions(txnResponse.data.transactions || []);
      setSummary(summaryResponse.data);
    } catch (error) {
      console.error("Failed to fetch billing data:", error);
      toast.error("Failed to fetch billing records");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBillingData();
  }, [token]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const totalRevenue = summary?.total_revenue_display || "$0";

  return (
    <div className="space-y-6" data-testid="admin-billing">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-primary/10 border-primary/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Total Revenue</p>
                <p className="text-3xl font-heading font-bold text-white">
                  {totalRevenue}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-navy-900/50 border-slate-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Transactions</p>
                <p className="text-3xl font-heading font-bold text-white">
                  {summary?.transaction_count || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-slate-700/50 flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-slate-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-navy-900/50 border-slate-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Unique Customers</p>
                <p className="text-3xl font-heading font-bold text-white">
                  {summary?.unique_customer_count || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-slate-700/50 flex items-center justify-center">
                <Users className="w-6 h-6 text-slate-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Billing Transactions */}
      <Card className="bg-navy-900/50 border-slate-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-white">
              <CreditCard className="w-5 h-5 text-primary" />
              Billing Transactions ({transactions.length})
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchBillingData}
              className="text-slate-400"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {transactions.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700 hover:bg-transparent">
                    <TableHead className="text-slate-400">User Email</TableHead>
                    <TableHead className="text-slate-400">Service</TableHead>
                    <TableHead className="text-slate-400">Amount</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">SER ID</TableHead>
                    <TableHead className="text-slate-400">Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((txn) => (
                    <TableRow
                      key={txn.txn_id}
                      className="border-slate-800 hover:bg-navy-800/50"
                      data-testid={`billing-row-${txn.txn_id}`}
                    >
                      <TableCell className="text-white font-medium">
                        {txn.user_email}
                      </TableCell>
                      <TableCell className="text-slate-300 text-sm">
                        {txn.service_name || txn.service_public_id}
                      </TableCell>
                      <TableCell className="text-white font-medium">
                        ${(txn.amount_cents / 100).toLocaleString()} {(txn.currency || 'USD').toUpperCase()}
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          txn.status === "SUCCEEDED" 
                            ? "bg-green-500/20 text-green-400 border-green-500/30"
                            : txn.status === "FAILED"
                              ? "bg-red-500/20 text-red-400 border-red-500/30"
                              : "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
                        }>
                          {txn.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-500 text-xs font-mono max-w-[150px] truncate">
                        {txn.ser_id?.slice(0, 8)}...
                      </TableCell>
                      <TableCell className="text-slate-400 text-sm">
                        {new Date(txn.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">No billing transactions yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminBilling;
